import React, { useContext, useEffect } from 'react'
import { Modal, Form, Input, Select, Switch, Button, Space, Alert, InputNumber } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { GET_TEAMS } from '../graphql/Queries'
import { GET_USERS_FOR_SELECT } from 'modules/Users/graphql/Queries'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const { Option } = Select

const MemberModal = ({ visible, onCancel, onOk, editingMember }) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canSubmit = editingMember ? (hasEdit && hasSave) : (hasAdd && hasSave)

  const [form] = Form.useForm()
  const { data: teamData, loading: teamLoading } = useQuery(GET_TEAMS)
  const { data: userData, loading: userLoading } = useQuery(GET_USERS_FOR_SELECT, {
    variables: {
      where: { isActive: 'true', role: ['STAFF', 'SALES_EXECUTIVE', 'ADMIN'] },
      skip: 0,
      limit: 100000,
    },
  })
  const teams = teamData?.getTeams || []
  const users = userData?.getUsers?.users || []

  let initialValues = editingMember
    ? {
        ...editingMember,
        teamId: editingMember.team?.id,
        userId: editingMember.user?.id,
      }
    : {}
  useEffect(() => {
    if (editingMember) {
      form.setFieldsValue(initialValues)
    } else {
      form.resetFields()
    }
  }, [editingMember, form, initialValues])

  const handleSubmit = () => {
    if (!canSubmit) return
    form.validateFields().then((values) => {
      onOk(values)
    })
  }

  return (
    <Modal
      title={editingMember ? 'Edit Member' : 'Add Member'}
      visible={visible}
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
      footer={null}
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={initialValues ? initialValues : {}}
      >
        <Form.Item
          label='Team'
          name='teamId'
          rules={[{ required: true, message: 'Please select team' }]}
        >
          <Select
            showSearch
            loading={teamLoading}
            placeholder='Select Team'
            optionFilterProp='children'
            filterOption={(input, option) =>
              String(option?.children)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            allowClear
          >
            {teams.map((team) => (
              <Option value={team.id} key={team.id}>
                {team.teamName}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label='User'
          name='userId'
          rules={[{ required: true, message: 'Please select user' }]}
        >
          <Select
            showSearch
            loading={userLoading}
            placeholder='Select User'
            optionFilterProp='children'
            filterOption={(input, option) => {
              const user = users
                .filter(
                  (u) => u.role !== 'ASSOCIATE' && u.role !== 'MASTER ASSOCIATE'
                )
                .find((u) => u.id === option.value)

              if (!user) return false
              const searchText = `${user.firstName} ${user.lastName || ''} ${
                user.role
              }`.toLowerCase()
              return searchText.includes(input.toLowerCase())
            }}
            allowClear
          >
            {users.map((user) => (
              <Option value={user.id} key={user.id}>
                <span
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>
                    {user.firstName} {user.lastName || ''}
                  </span>
                  <span
                    style={{
                      marginLeft: 8,
                      color: '#999',
                      fontStyle: 'italic',
                    }}
                  >
                    ({user.role})
                  </span>
                </span>
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label='Specialization'
          name='specialization'
          rules={[{ required: true, message: 'Please enter specialization' }]}
        >
          <Input placeholder='Enter Specialization' />
        </Form.Item>
        <Form.Item
          label='MaxTicketLoad'
          name='maxTicketLoad'
          rules={[{ required: true, message: 'Please enter MaxTicketLoad' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder='Enter MaxTicketLoad'
            min={0} // optional: no negatives
          />
        </Form.Item>

        <Alert
          message='Tip for multiple specializations'
          description='You can write multiple specializations separated by commas. e.g: invoice,payment,upi'
          type='info'
        />
        <Form.Item label='Available' name='isAvailable' valuePropName='checked'>
          <Switch />
        </Form.Item>
        {/* <Form.Item label='Ticket Load' name='ticketLoad'>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item> */}
        <Form.Item>
          <Space
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              onClick={() => {
                form.resetFields()
                onCancel()
              }}
            >
              Cancel
            </Button>
            <Button type='primary' onClick={handleSubmit} disabled={!canSubmit}>
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default MemberModal
